import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  Grid,
  Alert,
  alpha,
  Link,
} from "@mui/material";
import {
  TrendingUp as CreditIcon,
  TrendingDown as DebitIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as VolumeIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  transactionAPI,
  Transaction,
  TransactionSortField,
} from "../../services/transactionAPI";
import { branchAPI } from "../../services/branchAPI";
import { useAuthStore } from "../../stores/authStore";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import HolderAvatar from "../../components/layout/HolderAvatar";
import TableActionButton from "../../components/layout/TableActionButton";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { Visibility as ViewIcon } from "@mui/icons-material";
import { tokens } from "../../theme";

type TypeFilter = "all" | "debit" | "credit";

const formatCurrency = (amount: number | string) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), "dd MMM yyyy, HH:mm");
  } catch {
    return dateString;
  }
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accent: string;
  iconBg: string;
}> = ({ title, value, icon, accent, iconBg }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: tokens.radius.md,
      border: "1px solid",
      borderColor: "divider",
      height: "100%",
      bgcolor: "background.paper",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: tokens.radius.sm,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: iconBg,
          color: accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" display="block">
          {title}
        </Typography>
        <Typography variant="h6" fontWeight={700} noWrap>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const role = String(user?.role || "").toUpperCase();
  const isSuperAdmin = role === "SUPER_ADMIN" || role === "SUPERADMIN" || role === "PLATFORM_ADMIN";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<TransactionSortField>("transactionDate", "desc");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
    enabled: isSuperAdmin,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "transactions",
      page,
      rowsPerPage,
      typeFilter,
      branchFilter,
      debouncedSearch,
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      transactionAPI.getTransactions({
        pageSize: rowsPerPage,
        pageNo: page + 1,
        type: typeFilter === "all" ? undefined : typeFilter,
        branchId: branchFilter !== "all" ? branchFilter : undefined,
        search: debouncedSearch || undefined,
        startDate: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
        endDate: endDate ? format(endDate, "yyyy-MM-dd") : undefined,
        ...sortParams,
      }),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const transactions = data?.transactions ?? [];
  const totalCount = data?.total ?? 0;

  const onSort = (field: TransactionSortField) =>
    handleSort(field, () => setPage(0));

  const openTransaction = (transactionId: string) =>
    navigate(`/transactions/${transactionId}`);

  const typeChip = (type: string) => (
    <Chip
      label={type === "credit" ? "Credit" : "Debit"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        bgcolor:
          type === "credit"
            ? alpha(tokens.colors.success.main, 0.12)
            : alpha(tokens.colors.error.main, 0.12),
        color:
          type === "credit" ? tokens.colors.success.main : tokens.colors.error.main,
        border: "none",
      }}
    />
  );

  const filterDropdowns = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      {isSuperAdmin && (
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel>Branch</InputLabel>
          <Select
            value={branchFilter}
            label="Branch"
            onChange={(e) => {
              setBranchFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Branches</MenuItem>
            {branches.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={typeFilter}
          label="Type"
          onChange={(e) => {
            setTypeFilter(e.target.value as TypeFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All types</MenuItem>
          <MenuItem value="credit">Credit</MenuItem>
          <MenuItem value="debit">Debit</MenuItem>
        </Select>
      </FormControl>
      <DatePicker
        label="From"
        value={startDate}
        onChange={(d) => {
          setStartDate(d);
          setPage(0);
        }}
        slotProps={{ textField: { size: "small", sx: { minWidth: { sm: 150 } } } }}
      />
      <DatePicker
        label="To"
        value={endDate}
        onChange={(d) => {
          setEndDate(d);
          setPage(0);
        }}
        slotProps={{ textField: { size: "small", sx: { minWidth: { sm: 150 } } } }}
      />
    </Stack>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PageHeader
          title="Transactions"
          subtitle="NFC card credits, debits, and payment history"
          breadcrumbs={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Transactions" },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as Error).message || "Failed to load transactions"}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total transactions"
              value={totalCount.toLocaleString("en-IN")}
              icon={<ReceiptIcon />}
              accent={tokens.colors.primary.main}
              iconBg={alpha(tokens.colors.primary.main, 0.1)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Credit volume"
              value={formatCurrency(data?.totalCredit ?? 0)}
              icon={<VolumeIcon />}
              accent={tokens.colors.success.main}
              iconBg={alpha(tokens.colors.success.main, 0.12)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Credit count"
              value={(data?.creditCount ?? 0).toLocaleString("en-IN")}
              icon={<CreditIcon />}
              accent={tokens.colors.success.main}
              iconBg={alpha(tokens.colors.success.main, 0.12)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Debit count"
              value={(data?.debitCount ?? 0).toLocaleString("en-IN")}
              icon={<DebitIcon />}
              accent={tokens.colors.error.main}
              iconBg={alpha(tokens.colors.error.main, 0.12)}
            />
          </Grid>
        </Grid>

        <DataListCard
          searchPlaceholder="Search holder, phone, branch…"
          searchValue={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(0);
          }}
          filters={filterDropdowns}
          footer={
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ width: "100%" }}
            />
          }
        >
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="text.secondary">Loading transactions…</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table
                size="small"
                sx={{ width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell width={52}>S.No.</TableCell>
                    <SortableTableCell
                      label="Card holder"
                      field="holderName"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={onSort}
                    />
                    <SortableTableCell
                      label="Type"
                      field="type"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={onSort}
                    />
                    <SortableTableCell
                      label="Amount"
                      field="amount"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={onSort}
                    />
                    <SortableTableCell
                      label="Balance before"
                      field="balanceBefore"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={onSort}
                    />
                    <SortableTableCell
                      label="Balance after"
                      field="balanceAfter"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={onSort}
                    />
                    <SortableTableCell
                      label="Date"
                      field="transactionDate"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={onSort}
                    />
                    <SortableTableCell
                      label="Branch"
                      field="branch"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={onSort}
                    />
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction: Transaction, index: number) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <HolderAvatar
                            name={transaction.cardHolder?.holderName || "?"}
                            size={36}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {transaction.cardHolder?.holderName || "—"}
                            </Typography>
                            {transaction.cardHolder?.phone && (
                              <Typography variant="caption" color="text.secondary">
                                {transaction.cardHolder.phone}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{typeChip(transaction.type)}</TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => openTransaction(transaction.id)}
                          sx={{
                            fontWeight: 600,
                            border: "none",
                            background: "none",
                            p: 0,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            color:
                              transaction.type === "credit"
                                ? "success.main"
                                : "error.main",
                            textAlign: "left",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {transaction.type === "credit" ? "+" : "−"}
                          {formatCurrency(Math.abs(Number(transaction.amount)))}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(transaction.balanceBefore)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(transaction.balanceAfter)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(transaction.transactionDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transaction.branch?.name || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <TableActionButton
                          title="View details"
                          onClick={() => openTransaction(transaction.id)}
                        >
                          <ViewIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          No transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataListCard>
      </Box>
    </LocalizationProvider>
  );
};

export default TransactionsPage;
